import React, { createContext, useContext, useState, useEffect } from 'react';

const AccessibilityContext = createContext();

export const useAccessibility = () => {
  const ctx = useContext(AccessibilityContext);
  if (!ctx) throw new Error('useAccessibility must be used within AccessibilityProvider');
  return ctx;
};

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'a11y-settings';
const HIDDEN_KEY = 'a11y-hidden';

const DEFAULTS = {
  fontScale: 'normal', // 'normal' | 'large' | 'xlarge'
  highContrast: false,
  reduceMotion: false,
  underlineLinks: false,
  readable: false,
  focusHighlight: false,
  focusReader: false, // ADHS / reading-mask: dim screen except a strip following the cursor
};

const loadSettings = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    return { ...DEFAULTS, reduceMotion: prefersReduced, ...saved };
  } catch {
    return { ...DEFAULTS };
  }
};

export const AccessibilityProvider = ({ children }) => {
  const [settings, setSettings] = useState(loadSettings);
  const [allowHide, setAllowHide] = useState(true);
  const [hidden, setHiddenState] = useState(() => localStorage.getItem(HIDDEN_KEY) === '1');

  // Load admin permission (whether users may hide the button at all)
  useEffect(() => {
    const fetchAllow = () =>
      fetch(`${API}/api/admin/settings/accessibility/public`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => { if (d && typeof d.allow_hide_button === 'boolean') setAllowHide(d.allow_hide_button); })
        .catch(() => {});
    fetchAllow();
    const onVisible = () => { if (document.visibilityState === 'visible') fetchAllow(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-a11y-font', settings.fontScale);
    root.classList.toggle('a11y-contrast', settings.highContrast);
    root.classList.toggle('a11y-reduce-motion', settings.reduceMotion);
    root.classList.toggle('a11y-underline-links', settings.underlineLinks);
    root.classList.toggle('a11y-readable', settings.readable);
    root.classList.toggle('a11y-focus', settings.focusHighlight);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  const setHidden = (val) => {
    setHiddenState(val);
    localStorage.setItem(HIDDEN_KEY, val ? '1' : '0');
  };

  // Keyboard shortcut (Alt+Shift+A) to re-show / toggle the button visibility.
  useEffect(() => {
    const onKey = (e) => {
      if (e.altKey && e.shiftKey && (e.key === 'A' || e.key === 'a')) {
        e.preventDefault();
        setHidden(!(localStorage.getItem(HIDDEN_KEY) === '1'));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const update = (patch) => setSettings((prev) => ({ ...prev, ...patch }));
  const toggle = (key) => setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  const reset = () => setSettings({ ...DEFAULTS });
  const isActive =
    settings.fontScale !== 'normal' ||
    settings.highContrast ||
    settings.underlineLinks ||
    settings.readable ||
    settings.focusHighlight ||
    settings.focusReader;

  // Effective visibility: only hidden if admin allows it AND the user chose to hide.
  const buttonHidden = allowHide && hidden;

  return (
    <AccessibilityContext.Provider
      value={{ settings, update, toggle, reset, isActive, allowHide, hidden: buttonHidden, setHidden }}
    >
      {children}
    </AccessibilityContext.Provider>
  );
};
